import React from "react";
import cname from "../../style.module.scss";
import { ZeroButton, PowerButton } from "../../SpectrophotometerImages";
import { throttle } from "@learningblocks/exp-constants/lib/Utilities";

const THROTTLE_DELAY = 250;

interface ControllerProps {
  /**
   * Absorbance value of solution
   */
  absorbanceValue: number;
  /**
   * Relative absorbance value set
   */
  relativeAbsorbance: number;
  /**
   * Callback for PowerButton click
   */
  onClickPowerButton?: (_: boolean) => void;
  /**
   * Callback for ZeroButton click
   */
  onClickZeroButton?: (_: number) => void;
  /**
   * Current wavelength
   */
  wavelength: number;
  /**
   * Callback for wavelength slider change
   */
  setWavelength?: (_: number) => void;
  /**
   * Boolean to set if spectrophotometer is on/off
   */
  poweredUp: boolean;
  /**
   * Boolean to disable interaction
   */
  disabled?: boolean;
}

export function Controller(props: ControllerProps) {
  const [value, setValue] = React.useState(props.wavelength);

  React.useEffect(() => {
    if (value !== props.wavelength) {
      setValue(props.wavelength);
    }
  }, [props.wavelength]);

  const renderAbsorbance = () => {
    let absorbance = props.absorbanceValue;
    if (props.absorbanceValue) {
      absorbance = props.absorbanceValue;
    }
    return absorbance.toFixed(3);
  };

  const onClickZeroButton = () =>
    props.onClickZeroButton?.(props.absorbanceValue);

  const onClickPowerButton = () => props.onClickPowerButton?.(!props.poweredUp);

  const onInputChange = (
    event: React.ChangeEvent<{ valueAsNumber: number }>
  ) => {
    if (props.poweredUp && !props.disabled) {
      const newValue = event.target.valueAsNumber;
      setValue(newValue);
      throttle(() => {
        props.setWavelength?.(newValue);
      }, THROTTLE_DELAY);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!props.poweredUp || props.disabled) {
      event.preventDefault();
      return;
    }
  };

  const setDisplayClassName = () => {
    if (props.poweredUp) return `${cname.displayBlock} ${cname.poweredUp}`;
    else return `${cname.displayBlock}`;
  };

  const setDisplayAltText = () => {
    return props.poweredUp
      ? `Spectrophotometer display. Wavelength: ${value} nanometers. Absorbance: ${renderAbsorbance()}.`
      : `Spectrophotometer display. Powered off`;
  };

  return (
    <div className={cname.controller}>
      <div className={setDisplayClassName()} aria-label={setDisplayAltText()}>
        {props.poweredUp && (
          <>
            <p className={cname.wavelength}> WAVELENGTH: {value}nm</p>
            <p className={cname.absorbanceTitle}>ABSORBANCE</p>
            <p className={cname.absorbanceValue}>{renderAbsorbance()}</p>
          </>
        )}
      </div>
      <div className={cname.waveBlock}>
        <p className={cname.title}>Wavelength (nm)</p>
        <div
          className={cname.waveMeter}
          aria-label={`Wavelength slider which runs from 350 nanometers to 750 nanometers. Use the [left and right] arrow keys to adjust the wavelength.`}
        >
          <div className={cname.scale}>
            <span>350</span>
            <span>400</span>
            <span>450</span>
            <span className={cname.black}>500</span>
            <span className={cname.black}>550</span>
            <span className={cname.black}>600</span>
            <span className={cname.black}>650</span>
            <span className={cname.black}>700</span>
            <span>750</span>
          </div>
          <input
            type="range"
            min="350"
            max="750"
            value={value}
            step="5"
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            tabIndex={props.poweredUp && !props.disabled ? 0 : -1}
            disabled={!props.poweredUp || props.disabled}
            aria-label={`Wavelength slider. Current value: ${value} nanometers. Use arrow keys to adjust.`}
          />
        </div>
      </div>
      {!props.disabled && (
        <div className={cname.buttonBlock}>
          <ZeroButton
            className={cname.zerobutton}
            onClick={onClickZeroButton}
          />
          <PowerButton
            className={cname.powerbutton}
            onClick={onClickPowerButton}
          />
        </div>
      )}
    </div>
  );
}

Controller.defaultProps = {
  absorbanceValue: 0,
  relativeAbsorbance: 0,
};
